import React, { memo } from "react";

import EnterpriseReportSummary from "./EnterpriseReportSummary";

export const formatSummaryNumber = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toLocaleString() : "NA";
};

export const formatSummaryPercent = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(2)}%` : "NA";
};

export const getCurrentHourValue = (chart = [], key, currentHour, fallback = 0) => {
  const value = chart.find((item) => item?.hour === currentHour)?.[key];
  return Number.isFinite(Number(value)) && value !== null ? value : fallback;
};

export const sumSummaryRows = (rows = [], key) =>
  rows.reduce((total, row) => total + (Number(row?.[key]) || 0), 0);

export const averageSummaryRows = (rows = [], key) => {
  const validRows = rows.filter((row) => Number.isFinite(Number(row?.[key])));
  if (!validRows.length) return null;
  return sumSummaryRows(validRows, key) / validRows.length;
};

const getScopeName = (row = {}) =>
  row.scopeName ||
  row.branchName ||
  row.serviceName ||
  row.agentName ||
  row.name ||
  "Unknown";

const formatDurationValue = (formatDuration, value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "NA";
  return formatDuration ? formatDuration(numericValue) : formatSummaryNumber(numericValue);
};

export const buildRealtimeArrivalSummary = ({
  total = 0,
  average = 0,
  min = 0,
  max = 0,
  peakHour = "NA",
  currentHourTotal = 0,
  serviceData = [],
  formatNumber = formatSummaryNumber,
}) => {
  const rankedServices = [...serviceData]
    .sort((left, right) => Number(right.count || 0) - Number(left.count || 0))
    .slice(0, 8);

  return {
    kpis: [
      { label: "Arrivals Today", value: formatNumber(total) },
      { label: "This Hour", value: formatNumber(currentHourTotal) },
      { label: "Peak Hour", value: peakHour || "NA" },
      { label: "Avg / Hour", value: formatNumber(average) },
      { label: "Min / Hour", value: formatNumber(min) },
      { label: "Max / Hour", value: formatNumber(max) },
    ],
    tables: rankedServices.length
      ? [
          {
            title: "Top Services",
            columns: [
              { id: "name", label: "Service" },
              { id: "count", label: "Tickets" },
            ],
            rows: rankedServices,
            renderCell: (row, columnId) =>
              columnId === "count" ? formatNumber(row[columnId]) : row[columnId],
          },
        ]
      : [],
    notes: rankedServices[0]
      ? [`Highest current demand is ${rankedServices[0].name} with ${formatNumber(rankedServices[0].count)} tickets.`]
      : [],
  };
};

export const buildRealtimeDurationSummary = ({
  rows = [],
  scopeLabel = "Scope",
  durationLabel = "Duration",
  formatDuration,
}) => {
  const normalizedRows = rows.map((row) => {
    const totalTickets = Number(row.totalTickets ?? row.total ?? row.tickets ?? 0) || 0;
    const totalDuration =
      Number(row.totalDuration ?? row.totalWaitTime ?? row.totalServiceTime ?? 0) || 0;
    const average = Number(row.average ?? row.hourlyAverage ?? 0) || 0;
    const max = Number(row.max ?? 0) || 0;

    return {
      ...row,
      scopeName: getScopeName(row),
      totalTickets,
      totalDuration,
      average,
      max,
    };
  });
  const activeRows = normalizedRows.filter((row) => row.totalTickets > 0 || row.totalDuration > 0);
  const totalTickets = normalizedRows.reduce((sum, row) => sum + row.totalTickets, 0);
  const totalDuration = normalizedRows.reduce((sum, row) => sum + row.totalDuration, 0);
  const weightedAverage = totalTickets ? totalDuration / totalTickets : averageSummaryRows(activeRows, "average");
  const rankedRows = [...activeRows]
    .sort(
      (left, right) =>
        right.totalDuration - left.totalDuration ||
        right.average - left.average ||
        left.scopeName.localeCompare(right.scopeName)
    )
    .slice(0, 10);
  const slowest = [...activeRows].sort((left, right) => right.average - left.average)[0];

  return {
    kpis: [
      { label: "Tickets", value: formatSummaryNumber(totalTickets) },
      { label: `Total ${durationLabel}`, value: formatDurationValue(formatDuration, totalDuration) },
      { label: "Avg / Ticket", value: formatDurationValue(formatDuration, weightedAverage) },
      { label: `Highest ${durationLabel}`, value: rankedRows[0]?.scopeName || "NA" },
      { label: "Slowest Avg", value: slowest?.scopeName || "NA" },
    ],
    tables: rankedRows.length
      ? [
          {
            title: `${scopeLabel} ${durationLabel} Ranking`,
            columns: [
              { id: "scopeName", label: scopeLabel },
              { id: "totalTickets", label: "Tickets" },
              { id: "totalDuration", label: `Total ${durationLabel}` },
              { id: "average", label: "Average" },
              { id: "max", label: "Max" },
            ],
            rows: rankedRows,
            renderCell: (row, columnId) => {
              if (["totalDuration", "average", "max"].includes(columnId)) {
                return formatDurationValue(formatDuration, row[columnId]);
              }
              if (columnId === "totalTickets") return formatSummaryNumber(row[columnId]);
              return row[columnId];
            },
          },
        ]
      : [],
    notes: slowest ? [`Slowest average ${durationLabel.toLowerCase()}: ${slowest.scopeName}.`] : [],
  };
};

export const buildRealtimeDashboardSummary = ({
  totalTickets = 0,
  completedTickets = 0,
  noShowTickets = 0,
  activeTickets = 0,
  queuedTickets = 0,
  onlineAgents = 0,
  totalAgents = 0,
  branchesData = [],
  agentsData = [],
}) => {
  const completionPct = totalTickets ? (Number(completedTickets || 0) / Number(totalTickets)) * 100 : 0;
  const noShowPct = totalTickets ? (Number(noShowTickets || 0) / Number(totalTickets)) * 100 : 0;
  const onlinePct = totalAgents ? (Number(onlineAgents || 0) / Number(totalAgents)) * 100 : 0;
  const rankedBranches = [...(branchesData || [])]
    .map((row) => ({
      ...row,
      scopeName: getScopeName(row),
      totalTickets: Number(row.totalTickets ?? row.total ?? row.numTickets ?? row.tickets ?? 0) || 0,
    }))
    .sort((left, right) => right.totalTickets - left.totalTickets || left.scopeName.localeCompare(right.scopeName))
    .slice(0, 5);
  const rankedAgents = [...(agentsData || [])]
    .map((row) => ({
      ...row,
      scopeName: getScopeName(row),
      totalTickets: Number(row.totalTickets ?? row.total ?? row.numTickets ?? row.tickets ?? 0) || 0,
    }))
    .sort((left, right) => right.totalTickets - left.totalTickets || left.scopeName.localeCompare(right.scopeName))
    .slice(0, 5);

  return {
    kpis: [
      { label: "Tickets Today", value: formatSummaryNumber(totalTickets) },
      { label: "Completed", value: formatSummaryNumber(completedTickets), note: formatSummaryPercent(completionPct) },
      { label: "No Show", value: formatSummaryNumber(noShowTickets), note: formatSummaryPercent(noShowPct) },
      { label: "Active Now", value: formatSummaryNumber(activeTickets) },
      { label: "Queued Now", value: formatSummaryNumber(queuedTickets) },
      { label: "Online Agents", value: `${formatSummaryNumber(onlineAgents)}/${formatSummaryNumber(totalAgents)}`, note: formatSummaryPercent(onlinePct) },
    ],
    tables: [
      {
        title: "Top Branches",
        columns: [
          { id: "scopeName", label: "Branch" },
          { id: "totalTickets", label: "Tickets" },
        ],
        rows: rankedBranches,
        renderCell: (row, columnId) =>
          columnId === "totalTickets" ? formatSummaryNumber(row[columnId]) : row[columnId],
      },
      {
        title: "Top Agents",
        columns: [
          { id: "scopeName", label: "Agent" },
          { id: "totalTickets", label: "Tickets" },
        ],
        rows: rankedAgents,
        renderCell: (row, columnId) =>
          columnId === "totalTickets" ? formatSummaryNumber(row[columnId]) : row[columnId],
      },
    ].filter((table) => table.rows.length),
    notes: queuedTickets
      ? [`There are ${formatSummaryNumber(queuedTickets)} customers currently waiting in queue.`]
      : [],
  };
};

const RealtimeEnterpriseSummary = ({
  title = "Enterprise Realtime Summary",
  kpis = [],
  tables = [],
  notes = [],
  maxRows = 5,
}) => {
  const compactTables = tables
    .map((table) => ({
      ...table,
      rows: (table.rows || []).filter(Boolean).slice(0, table.maxRows || maxRows),
    }))
    .filter((table) => table.columns?.length && table.rows.length);

  if (!kpis.length && !compactTables.length && !notes.length) return null;

  return (
    <EnterpriseReportSummary
      title={title}
      kpis={kpis}
      tables={compactTables}
      notes={notes}
    />
  );
};

export default memo(RealtimeEnterpriseSummary);
